import type { CodeMigration } from './types.js';

const CANONICAL_INDEX_CSS = `@import "tailwindcss";
@import "@atlashub/smartstack/theme.css";
@source "../node_modules/@atlashub/smartstack/dist/**/*.js";
`;

const VITE_PLUGIN_HELPER = `import { readFile } from 'node:fs/promises';
import tailwindcss from '@tailwindcss/vite';

// Upstream workaround: @atlashub/smartstack's theme.css contains a comment
// with the pattern \`**/*.js\` whose \`*/\` closes the comment early and breaks
// Tailwind v4's CSS parser ("Unterminated string: ';'"). Rewrite on load.
function fixSmartstackThemeComment() {
  return {
    name: 'fix-smartstack-theme-comment',
    enforce: 'pre',
    async load(id) {
      const normalized = id.split('?')[0].replace(/\\\\/g, '/');
      if (!normalized.endsWith('@atlashub/smartstack/dist/theme.css')) return null;
      const content = await readFile(normalized, 'utf-8');
      return content.replace(/\\*\\*\\/\\*\\.js/g, '** /*.js');
    },
  };
}`;

export const indexCssTailwindV4Migration: CodeMigration = {
  fromVersion: '1.0.0',
  toVersion: '4.81.0',
  description: 'Fix index.css/main.tsx/vite.config.ts for Tailwind v4 + SmartStack theme.css upstream bug',

  migrations: [
    {
      pattern: '**/web/**/src/index.css',
      description: 'Rewrite index.css to canonical 3-line form (imports tailwindcss + theme.css, scans package bundle)',

      detect: (content: string): boolean => {
        const hasTailwind = content.includes('@import "tailwindcss"');
        if (!hasTailwind) return false;
        return content.trim() !== CANONICAL_INDEX_CSS.trim();
      },

      transform: (): string => CANONICAL_INDEX_CSS,
    },
    {
      pattern: '**/web/**/src/main.tsx',
      description: 'Remove invalid @atlashub/smartstack/styles.css import (export does not exist)',

      detect: (content: string): boolean => {
        return /import\s+['"]@atlashub\/smartstack\/styles\.css['"];?\s*$/m.test(content);
      },

      transform: (content: string): string => {
        return content.replace(/^import\s+['"]@atlashub\/smartstack\/styles\.css['"];?\s*\r?\n/m, '');
      },
    },
    {
      pattern: '**/web/**/vite.config.ts',
      description: 'Inject fixSmartstackThemeComment plugin to neutralize theme.css upstream comment bug',

      detect: (content: string): boolean => {
        const usesTailwindVite = content.includes('@tailwindcss/vite');
        const hasFix = content.includes('fixSmartstackThemeComment');
        return usesTailwindVite && !hasFix;
      },

      transform: (content: string): string => {
        let updated = content;

        if (!updated.includes('fixSmartstackThemeComment')) {
          updated = updated.replace(
            /import\s+tailwindcss\s+from\s+['"]@tailwindcss\/vite['"];?\s*\r?\n/,
            `${VITE_PLUGIN_HELPER}\n\n`
          );
        }

        updated = updated.replace(
          /plugins:\s*\[\s*react\(\)\s*,\s*tailwindcss\(\)\s*\]/,
          'plugins: [react(), fixSmartstackThemeComment(), tailwindcss()]'
        );

        return updated;
      },
    },
  ],
};
